import { EntryLines } from './Journal';
import { When } from './Chat';

// Looking back over a finished game (the engine's Review.js), a position at a time: the game after that many moves.
// Big steps go from the start of one turn to the start of another (beside whose turn it is, ReviewSteps); little steps
// go a move at a time, through the turn's list of moves (in place of the actions, ReviewTurn).

// Whose turn it is, and the big steps: to the game's start, the turn before (or this one's start), the next, the end
export function ReviewSteps({Review, Position, OnGo, PlayerColors})
{
    const index = Review.TurnAt(Position);
    const turn = Review.turns[index];
    const atEnd = Position === Review.Length;
    const previous = Position > turn.start ? turn.start : Review.turns[index - 1]?.start ?? 0;
    const next = Review.turns[index + 1]?.start ?? Review.Length;
    const label = (name, player, round) => <><span style={{ color: PlayerColors[player] }}>{name}</span>'s turn, round {round}</>;
    // (as wide as the widest it gets, every player's name with the last round, so the buttons beside it stay put)
    const lastRound = Math.max(...Review.turns.map(t => t.round));
    const widest = [...new Map(Review.turns.map(t => [t.player, t.name])).entries()];
    return <span className="tta-review-steps">
        <span className="tta-review-label">
            {widest.map(([player, name]) => <span key={player} className="tta-review-sizer" aria-hidden="true">{label(name, player, lastRound)}</span>)}
            <span className="tta-review-sizer" aria-hidden="true">The game as it ended</span>
            <span>{atEnd ? 'The game as it ended' : label(turn.name, turn.player, turn.round)}</span>
        </span>
        <span className="tta-review-buttons">
            <button type="button" className="tta-ghost-button" title="The game as it began" disabled={Position === 0} onClick={() => OnGo(0)}>⏮</button>
            <button type="button" className="tta-ghost-button" title={Position > turn.start ? 'The start of this turn' : 'The turn before'}
                disabled={Position === 0} onClick={() => OnGo(previous)}>◀ Turn</button>
            <span className="tta-review-count">Turn {index + 1} of {Review.turns.length}</span>
            <button type="button" className="tta-ghost-button" title="The next turn" disabled={atEnd} onClick={() => OnGo(next)}>Turn ▶</button>
            <button type="button" className="tta-ghost-button" title="The game as it ended" disabled={atEnd} onClick={() => OnGo(Review.Length)}>⏭</button>
        </span>
    </span>;
}

// The turn's moves (everyone's: the decisions made during it too). The one marked is the one about to be made: the board
// is the game just before it, and the next step makes it (so a turn begins with its first move marked). A move at a
// time with the arrows, or to just before one by clicking it.
export function ReviewTurn({Review, Position, OnGo, PlayerColors, OnLeave})
{
    const index = Review.TurnAt(Position);
    const { start, end } = Review.TurnSpan(index);
    // (position: the game once the move is made)
    const moves = Review.moves.slice(start, end).map((entry, offset) => ({ ...entry, position: start + offset + 1 }))
        .filter(entry => !entry.back);
    const next = moves.find(entry => entry.position > Position);
    return <div className="tta tta-left tta-review-turn">
        <div className="tta-review-turn-head">
            <span className="tta-section-title">This turn</span>
            <span className="tta-review-buttons">
                <button type="button" className="tta-ghost-button" title="The move before" disabled={Position === 0} onClick={() => OnGo(Position - 1)}>◀</button>
                <span className="tta-review-count">Move {next ? next.position - start : end - start} of {end - start}</span>
                <button type="button" className="tta-ghost-button" title="The next move" disabled={Position === Review.Length} onClick={() => OnGo(Position + 1)}>▶</button>
            </span>
            <button type="button" className="tta-ghost-button tta-review-leave" onClick={OnLeave}>Back to the game</button>
        </div>
        {Review.error && <div className="tta-lobby-error">{Review.error}</div>}
        <ol className="tta-review-moves">
            {moves.map(entry => <li key={entry.position} onClick={() => OnGo(entry.position - 1)}
                className={`${entry === next ? 'current' : entry.position <= Position ? 'done' : 'later'}${entry.takenBack ? ' taken-back' : ''}`}
                title={entry.takenBack ? 'Taken back, to choose again' : undefined}>
                <span className="tta-review-who" style={{ color: PlayerColors[entry.player] }}>{entry.name}</span>
                <span className="tta-review-what"><EntryLines text={entry.description || '(no description)'}/></span>
                {entry.at && <span className="tta-review-when tta-muted">{When(entry.at)}</span>}
            </li>)}
        </ol>
    </div>;
}
